import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, IconName } from './icons';
import { AMBER, CONTROL, DIM, DIVIDER, FAMILY, Family, INK, MUTED, RED, ROW_ERROR, StatusInfo, TEAL, TEXT, statusOf } from './tokens';

/* ---------------- Buttons ---------------- */

type ButtonVariant = 'primary' | 'secondary' | 'text' | 'danger';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  phone?: boolean;
  icon?: IconName;
  trailingIcon?: IconName;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
}

const VARIANT: Record<ButtonVariant, React.CSSProperties> = {
  primary: { border: `1px solid ${TEAL}`, background: TEAL, color: '#FFFFFF' },
  secondary: { border: `1px solid ${CONTROL}`, background: '#FFFFFF', color: TEXT },
  text: { border: 0, background: 'transparent', color: TEAL },
  danger: { border: `1px solid ${CONTROL}`, background: '#FFFFFF', color: RED },
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', phone, icon, trailingIcon, fullWidth, type = 'button', style, children, className, ...rest }, ref) => {
    const text = variant === 'text';
    return (
      <button
        ref={ref}
        type={type}
        className={`pu-btn-${variant === 'danger' ? 'danger' : variant}${className ? ` ${className}` : ''}`}
        style={{
          width: fullWidth ? '100%' : undefined,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          height: text ? undefined : phone ? '48px' : '36px',
          minHeight: text ? (phone ? '44px' : '36px') : undefined,
          padding: text ? '0 10px' : phone ? '0 20px' : trailingIcon ? '0 14px 0 18px' : '0 18px',
          borderRadius: '999px',
          fontSize: phone ? '15px' : '14px',
          lineHeight: '20px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          ...VARIANT[variant],
          ...style,
        }}
        {...rest}
      >
        {icon && <Icon name={icon} />}
        {children}
        {trailingIcon && <Icon name={trailingIcon} color={MUTED} />}
      </button>
    );
  },
);
Button.displayName = 'Button';

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  icon: IconName;
  label: string;
  phone?: boolean;
  remove?: boolean;
  bordered?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(({ icon, label, phone, remove, bordered, style, ...rest }, ref) => {
  const size = phone ? 44 : bordered ? 36 : 32;
  return (
    <button
      ref={ref}
      type="button"
      className={remove ? 'pu-btn-remove' : bordered ? 'pu-btn-secondary' : 'pu-btn-icon'}
      aria-label={label}
      title={remove ? label : undefined}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: bordered ? `1px solid ${CONTROL}` : 0,
        borderRadius: '999px',
        background: bordered ? '#FFFFFF' : 'transparent',
        color: bordered ? MUTED : DIM,
        cursor: 'pointer',
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} size={remove ? (phone ? 20 : 18) : bordered ? 18 : 20} />
    </button>
  );
});
IconButton.displayName = 'IconButton';

/* ---------------- Pills and chips ---------------- */

export const Pill: React.FC<{ label: string; family: Family; dot?: boolean }> = ({ label, family, dot = true }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '2px 10px',
      borderRadius: '999px',
      background: FAMILY[family].bg,
      color: FAMILY[family].fg,
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    }}
  >
    {dot && <span aria-hidden="true" style={{ width: '6px', height: '6px', flex: 'none', borderRadius: '999px', background: FAMILY[family].fg }} />}
    {label}
  </span>
);

/** An order status by its id, or any document's status passed as `status`. */
export const StatusPill: React.FC<{ statusId?: unknown; status?: StatusInfo; t: (key: string) => string; dot?: boolean }> = ({ statusId, status, t, dot }) => {
  const shown = status ?? statusOf(statusId);
  return <Pill label={t(shown.label)} family={shown.family} dot={dot} />;
};

export const Chip: React.FC<{ pressed: boolean; phone?: boolean; onClick: () => void; children: React.ReactNode }> = ({ pressed, phone, onClick, children }) => (
  <button
    type="button"
    className="pu-chip"
    aria-pressed={pressed}
    onClick={onClick}
    style={{
      height: phone ? '44px' : '32px',
      padding: phone ? '0 16px' : '0 14px',
      border: `1px solid ${pressed ? TEAL : CONTROL}`,
      borderRadius: '999px',
      background: pressed ? TEAL : '#FFFFFF',
      color: pressed ? '#FFFFFF' : TEXT,
      fontSize: phone ? '15px' : '13px',
      lineHeight: phone ? '20px' : '18px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

/* ---------------- Fields ---------------- */

export const fieldStyle = (phone?: boolean, invalid?: boolean): React.CSSProperties => ({
  width: '100%',
  height: phone ? '44px' : '40px',
  padding: '0 12px',
  border: `1px solid ${invalid ? RED : CONTROL}`,
  borderRadius: phone ? '12px' : '10px',
  background: invalid ? ROW_ERROR : '#FFFFFF',
  fontSize: phone ? '16px' : '14px',
  lineHeight: phone ? '22px' : '20px',
  color: TEXT,
});

export const numberStyle = (phone?: boolean, invalid?: boolean): React.CSSProperties => ({
  ...fieldStyle(phone, invalid),
  padding: phone ? '0 12px' : '0 10px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
});

export const labelStyle: React.CSSProperties = { fontSize: '12.5px', lineHeight: '18px', fontWeight: 500, color: MUTED };

export const SearchField: React.FC<{
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  phone?: boolean;
  width?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onEnter?: () => void;
}> = ({ label, placeholder, value, onChange, phone, width, inputRef, onEnter }) => (
  <label style={{ position: 'relative', display: 'block', flex: width ? `0 1 ${width}` : '1 1 auto', minWidth: 0, width: width ? undefined : '100%' }}>
    <span className="pu-sr">{label}</span>
    <Icon name="search" size={phone ? 18 : 16} color={DIM} style={{ position: 'absolute', left: '12px', top: phone ? '13px' : '12px' }} />
    <input
      ref={inputRef}
      className="pu-input"
      type="search"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onEnter) {
          event.preventDefault();
          onEnter();
        }
      }}
      style={{ ...fieldStyle(phone), padding: phone ? '0 12px 0 38px' : '0 12px 0 36px', appearance: 'none' }}
    />
  </label>
);

export const SelectField: React.FC<
  React.SelectHTMLAttributes<HTMLSelectElement> & { phone?: boolean; invalid?: boolean; wrapperStyle?: React.CSSProperties }
> = ({ phone, invalid, wrapperStyle, style, children, ...rest }) => (
  <div style={{ position: 'relative', ...wrapperStyle }}>
    <select
      className="pu-input"
      style={{ ...fieldStyle(phone, invalid), padding: phone ? '0 32px 0 10px' : '0 30px 0 10px', appearance: 'none', cursor: 'pointer', textOverflow: 'ellipsis', ...style }}
      {...rest}
    >
      {children}
    </select>
    <Icon name="chevronDown" color={DIM} style={{ position: 'absolute', right: '10px', top: phone ? '14px' : '12px', pointerEvents: 'none' }} />
  </div>
);

export const FieldMessage: React.FC<{ id?: string; tone: 'error' | 'notice'; children: React.ReactNode; align?: 'left' | 'right' }> = ({
  id,
  tone,
  children,
  align = 'right',
}) => (
  <p id={id} style={{ margin: '4px 0 0', fontSize: '12px', lineHeight: '16px', color: tone === 'error' ? RED : AMBER, textAlign: align, fontVariantNumeric: 'tabular-nums' }}>
    {children}
  </p>
);

export const hintStyle: React.CSSProperties = { fontSize: '12.5px', lineHeight: '18px', color: MUTED };

/** A labelled control with its error under it, or a hint while there is none; the error is `${id}-error`. */
export const FormField: React.FC<{ id: string; label: string; error?: string; hint?: React.ReactNode; children: React.ReactNode }> = ({ id, label, error, hint, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label htmlFor={id} style={labelStyle}>
      {label}
    </label>
    {children}
    {error ? (
      <FieldMessage id={`${id}-error`} tone="error" align="left">
        {error}
      </FieldMessage>
    ) : (
      hint
    )}
  </div>
);

/* ---------------- Menu ---------------- */

export interface MenuItem {
  key: string;
  label: string;
  desc?: string;
  icon?: IconName;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/** A menu button: the panel opens under the trigger, right-aligned, in a portal so a card's overflow never clips it. */
export const Menu: React.FC<{
  label: string;
  items: MenuItem[];
  width?: number;
  trigger: (props: { onClick: () => void; 'aria-haspopup': 'menu'; 'aria-expanded': boolean }) => React.ReactNode;
}> = ({ label, items, width = 240, trigger }) => {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top?: number; bottom?: number; right: number }>({ right: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const itemButtons = () => Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) rootRef.current?.querySelector<HTMLElement>('button')?.focus();
  };
  const toggle = () => {
    if (open) return close(false);
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      // Opens upwards when the room under the trigger is short.
      const below = window.innerHeight - rect.bottom;
      const right = Math.max(8, window.innerWidth - rect.right);
      setPlace(below < 56 * items.length + 24 && rect.top > below ? { bottom: window.innerHeight - rect.top + 6, right } : { top: rect.bottom + 6, right });
    }
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    itemButtons()[0]?.focus();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) close(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      } else if (event.key === 'Tab') close(false);
    };
    const onMove = () => close(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const onPanelKey = (event: React.KeyboardEvent) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = itemButtons();
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (at + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };
  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      {trigger({ onClick: toggle, 'aria-haspopup': 'menu', 'aria-expanded': open })}
      {open &&
        createPortal(
          <div className="pu-scope">
            <div
              ref={panelRef}
              role="menu"
              aria-label={label}
              onKeyDown={onPanelKey}
              style={{
                position: 'fixed',
                top: place.top,
                bottom: place.bottom,
                right: place.right,
                zIndex: 210,
                width: `${width}px`,
                maxWidth: 'calc(100vw - 16px)',
                padding: '6px',
                background: '#FFFFFF',
                border: `1px solid ${DIVIDER}`,
                borderRadius: '12px',
                boxShadow: '0 12px 32px rgba(5,41,42,0.12)',
              }}
            >
              {items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className="pu-menu-item"
                  disabled={item.disabled}
                  onClick={() => {
                    close(false);
                    item.onSelect();
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: item.desc ? 'flex-start' : 'center',
                    gap: '10px',
                    padding: '8px 10px',
                    border: 0,
                    borderRadius: '8px',
                    background: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: item.danger ? RED : TEXT,
                  }}
                >
                  {item.icon && <Icon name={item.icon} color={item.danger ? RED : MUTED} style={{ marginTop: item.desc ? '2px' : 0 }} />}
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500 }}>{item.label}</span>
                    {item.desc && <span style={{ fontSize: '12.5px', lineHeight: '18px', color: MUTED }}>{item.desc}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

/* ---------------- States ---------------- */

export const EmptyState: React.FC<{ icon?: IconName; title: string; desc?: string; action?: React.ReactNode; phone?: boolean }> = ({
  icon = 'inbox',
  title,
  desc,
  action,
  phone,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      padding: phone ? '28px 16px' : '32px 16px',
      border: '1px dashed #DBE4E4',
      borderRadius: '12px',
      background: '#F9FBFB',
      textAlign: 'center',
    }}
  >
    <Icon name={icon} size={20} color={DIM} />
    <p style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: INK }}>{title}</p>
    {desc && <p style={{ fontSize: '13px', lineHeight: '18px', color: MUTED, maxWidth: '420px' }}>{desc}</p>}
    {action && <div style={{ marginTop: '4px' }}>{action}</div>}
  </div>
);

export const ErrorBanner: React.FC<{ message: string; retryLabel: string; onRetry: () => void }> = ({ message, retryLabel, onRetry }) => (
  <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '12px', background: ROW_ERROR, color: RED, fontSize: '14px', lineHeight: '20px' }}>
    <Icon name="alert" />
    <span style={{ flex: '1 1 auto', minWidth: 0 }}>{message}</span>
    <Button variant="text" onClick={onRetry} style={{ color: RED }}>
      {retryLabel}
    </Button>
  </div>
);

export const Skeleton: React.FC<{ width?: string; height?: number; radius?: number }> = ({ width = '100%', height = 12, radius = 12 }) => (
  <span className="pu-skeleton" aria-hidden="true" style={{ display: 'block', width, height: `${height}px`, borderRadius: `${radius}px`, background: '#EEF3F4' }} />
);
